/**
 * UserManagement.js
 * Admin interface for browsing the full user directory.
 *
 * Read-only in v1 — no edit, deactivate, or impersonate actions.
 * Deactivation already lives in the OrganizationDashboard seat panel where
 * it's org-domain-scoped; duplicating it here would lose that scoping.
 *
 * Backend: GET /api/admin/users (see routes/admin.js)
 *   Query params: search (email/name substring), tier (exact), activity (state).
 *   Response rows carry computed activityStatus: active | idle | dormant | deactivated.
 *
 * Layout / state conventions mirror WaitlistManagement.js.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { apiService } from '../../services/api';

const ACTIVITY_OPTIONS = [
  { value: 'all', label: 'All activity' },
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'Idle' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'deactivated', label: 'Deactivated' },
];

const SEARCH_DEBOUNCE_MS = 300;

const UserManagement = () => {
  // =========================================
  // State
  // =========================================
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter inputs (raw) vs. applied (debounced for search)
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');

  // Debounce search input → appliedSearch
  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // =========================================
  // Data fetching
  // =========================================
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {};
      if (appliedSearch) params.search = appliedSearch;
      if (tierFilter && tierFilter !== 'all') params.tier = tierFilter;
      if (activityFilter && activityFilter !== 'all') params.activity = activityFilter;
      const data = await apiService.admin.getUsers(params);
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching users directory:', err);
      setError(err.response?.data?.message || 'Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [appliedSearch, tierFilter, activityFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // =========================================
  // Derived: distinct tiers (from current response) for the tier dropdown
  // =========================================
  const tierOptions = useMemo(() => {
    const seen = new Set();
    users.forEach(u => { if (u.tier) seen.add(u.tier); });
    return ['all', ...Array.from(seen).sort()];
  }, [users]);

  // =========================================
  // Rendering
  // =========================================
  const formatName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—';
  const formatDate = (iso) => (iso ? format(new Date(iso), 'MM/dd/yyyy') : '—');

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h2>User Directory</h2>
      </div>

      <div className="user-filters">
        <input
          type="search"
          className="user-search-input"
          placeholder="Search email, first name, or last name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search users"
        />

        <select
          className="user-tier-filter"
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          aria-label="Filter by tier"
        >
          {tierOptions.map(t => (
            <option key={t} value={t}>
              {t === 'all' ? 'All tiers' : t}
            </option>
          ))}
        </select>

        <select
          className="user-activity-filter"
          value={activityFilter}
          onChange={(e) => setActivityFilter(e.target.value)}
          aria-label="Filter by activity"
        >
          {ACTIVITY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <p className="user-count-line">
        {isLoading ? 'Loading…' : `Showing ${users.length} user${users.length === 1 ? '' : 's'}`}
      </p>

      {error && <div className="error-message">{error}</div>}

      {!isLoading && users.length === 0 && !error ? (
        <p className="no-entries">No users match the current filters.</p>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Name</th>
              <th>Email</th>
              <th>Tier</th>
              <th>Activity</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u._id} className={u.isActive ? '' : 'deactivated-row'}>
                <td>{formatDate(u.createdAt)}</td>
                <td>{formatName(u)}</td>
                <td>{u.email}</td>
                <td>{u.tier || '—'}</td>
                <td>
                  <span className={`status-badge ${u.activityStatus}`}>
                    {u.activityStatus.charAt(0).toUpperCase() + u.activityStatus.slice(1)}
                  </span>
                </td>
                <td>
                  {u.isAdmin ? (
                    <span className="status-badge admin-badge">Admin</span>
                  ) : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default UserManagement;
